import { useRef, useEffect, useState, useCallback } from 'react'
import { useDMThread } from '../../hooks/useDMThread'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'
import { DMMessage } from './DMMessage'
import { DMTypingIndicator } from './DMTypingIndicator'
import { DMMessageInput } from './DMMessageInput'
import { DMReplyPreview } from './DMReplyPreview'
import { supabase } from '../../services/supabase'
import { AdvisorStatusDot } from './AdvisorStatusDot'

function dayLabel(dateStr) {
  const d = new Date(dateStr)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return 'Today'
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })
}

function groupByDay(messages) {
  const groups = []
  let lastDay = null
  for (const m of messages) {
    const day = m.created_at?.slice(0, 10)
    if (day !== lastDay) { groups.push({ type: 'day', label: dayLabel(m.created_at) }); lastDay = day }
    groups.push({ type: 'message', message: m })
  }
  return groups
}

export function DMThread({ conversationId, otherAdvisor, getStatus, getLastSeen, isDarkMode }) {
  const { advisor: me } = useCurrentAdvisor()
  const { messages, loading, hasMore, loadEarlier, sendMessage, editMessage, deleteMessage, toggleReaction } = useDMThread(conversationId)
  const [replyTo, setReplyTo] = useState(null)
  const [typingName, setTypingName] = useState(null)
  const bottomRef = useRef(null)
  const typingTimer = useRef(null)
  const channelRef = useRef(null)

  const status = otherAdvisor?.id ? getStatus(otherAdvisor.id) : null
  const otherName = otherAdvisor?.full_name || otherAdvisor?.name || 'Advisor'

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  // Typing indicator via broadcast channel
  useEffect(() => {
    if (!conversationId || !me?.id) return
    const ch = supabase.channel(`dm-typing-${conversationId}`)
      .on('broadcast', { event: 'typing' }, ({ payload }) => {
        if (payload.advisor_id === me.id) return
        setTypingName(payload.name)
        clearTimeout(typingTimer.current)
        typingTimer.current = setTimeout(() => setTypingName(null), 3000)
      })
      .subscribe()
    channelRef.current = ch
    return () => { supabase.removeChannel(ch); clearTimeout(typingTimer.current) }
  }, [conversationId, me?.id])

  const broadcastTyping = useCallback(() => {
    channelRef.current?.send({
      type: 'broadcast',
      event: 'typing',
      payload: { advisor_id: me?.id, name: me?.full_name || me?.name || 'Someone' }
    })
  }, [me])

  const items = groupByDay(messages)
  const isStandby = status === 'standby'

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className={`flex items-center gap-3 px-4 py-3 border-b flex-shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
        <AdvisorStatusDot
          avatarUrl={otherAdvisor?.avatar_url ?? null}
          fullName={otherName}
          size="sm"
          status={status}
          isDarkMode={isDarkMode}
        />
        <div>
          <p className={`text-sm font-semibold ${isDarkMode ? 'text-slate-100' : 'text-sfp-darkgray'}`}>{otherName}</p>
          {status === 'offline' && getLastSeen(otherAdvisor?.id) && (
            <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
              Last seen {new Date(getLastSeen(otherAdvisor.id)).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
          {status === 'online' && <p className="text-xs text-green-500">Online</p>}
          {status === 'standby' && <p className="text-xs text-yellow-500">Standby</p>}
        </div>
      </div>

      {/* Standby banner */}
      {isStandby && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-700 flex-shrink-0">
          {otherName} is currently unavailable — your message will be delivered.
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-2">
        {hasMore && (
          <div className="text-center py-2">
            <button
              onClick={loadEarlier}
              disabled={loading}
              className="text-xs text-sfp-orange hover:underline font-medium"
            >
              {loading ? 'Loading…' : 'Load earlier messages'}
            </button>
          </div>
        )}
        {items.map((item, idx) =>
          item.type === 'day' ? (
            <div key={`day-${idx}`} className="flex items-center gap-3 px-4 py-2">
              <div className={`flex-1 h-px ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />
              <span className={`text-xs flex-shrink-0 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>{item.label}</span>
              <div className={`flex-1 h-px ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />
            </div>
          ) : (
            <DMMessage
              key={item.message.id}
              message={item.message}
              isOwn={item.message.sender_id === me?.id}
              isDarkMode={isDarkMode}
              currentAdvisorId={me?.id}
              replyToMessage={item.message.reply_to_id ? messages.find(m => m.id === item.message.reply_to_id) : null}
              onReply={setReplyTo}
              onEdit={editMessage}
              onDelete={deleteMessage}
              onToggleReaction={toggleReaction}
            />
          )
        )}
        <DMTypingIndicator name={typingName} isDarkMode={isDarkMode} />
        <div ref={bottomRef} />
      </div>

      {/* Reply preview */}
      {replyTo && (
        <DMReplyPreview message={replyTo} onCancel={() => setReplyTo(null)} isDarkMode={isDarkMode} />
      )}

      {/* Input */}
      <DMMessageInput
        conversationId={conversationId}
        replyToId={replyTo?.id ?? null}
        onSend={async (payload) => { await sendMessage(payload); setReplyTo(null) }}
        onTyping={broadcastTyping}
        isDarkMode={isDarkMode}
      />
    </div>
  )
}
